import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { CriticalStandard } from '@/domain/types/collections';
import usePaginatedDocuments from '@/domain/use-cases/get-paginated-documents';
import Button from '@/presentation/components/button';
import LoadMoreButton from '@/presentation/components/load-more-button';
import Table, { Column } from '@/presentation/components/table';
import { AiFillFilePdf } from 'react-icons/ai';
import { FiPlus } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';

const CriticalStandarsPage = () => {
  const navigate = useNavigate();
  const { data, error, isLoading, isLoadingMore, hasMore, loadMore } =
    usePaginatedDocuments<CriticalStandard>(DOCUMENTS_DB.CRITICAL_STANDARS);

  const criticalStandardColumns: Column<CriticalStandard>[] = [
    { header: 'Título', accessor: 'title' },
    {
      header: 'Categoría',
      accessor: 'category',
      render: (value) => {
        const map: Record<string, string> = {
          critical: 'Estándares Críticos',
          'life-saving': 'Reglas para Salvar Vidas',
          tar: 'TAR',
          other: 'Otros Estándares',
        };
        return <span className="text-sm">{map[value as string] || '-'}</span>;
      },
    },
    {
      header: 'Descripción',
      accessor: 'description',
      render: (value) => {
        if (value && typeof value === 'string') {
          return (
            <span className="text-sm truncate block max-w-[400px]">
              {value}
            </span>
          );
        }
        return <span className="text-sm">No hay descripción</span>;
      },
    },
    {
      header: 'Creado el',
      accessor: 'createdAt',
      render: (value) => {
        if (value && typeof value === 'object' && 'seconds' in value) {
          return (
            <span className="text-sm">
              {new Date(value.seconds! * 1000).toLocaleDateString('es-CO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          );
        }
        return <span className="text-sm">Desconocido</span>;
      },
    },
    {
      header: 'Vistas',
      accessor: 'views',
      render: (value) => (
        <div className="flex justify-center items-center">
          <span className="text-sm">
            {value && typeof value === 'number' ? value : 0}
          </span>
        </div>
      ),
    },
    {
      header: 'Descargas',
      accessor: 'downloads',
      render: (value) => (
        <div className="flex justify-center items-center">
          <span className="text-sm">
            {value && typeof value === 'number' ? value : 0}
          </span>
        </div>
      ),
    },
    {
      header: 'Favoritos',
      accessor: 'favorites',
      render: (value) => (
        <div className="flex justify-center items-center">
          <span className="text-sm">
            {value && typeof value === 'number' ? value : 0}
          </span>
        </div>
      ),
    },
    {
      header: 'Comentarios',
      accessor: 'comments',
      render: (value) => (
        <div className="flex justify-center items-center">
          <span className="text-sm">
            {value && typeof value === 'number' ? value : 0}
          </span>
        </div>
      ),
    },
    {
      header: 'Documento',
      accessor: 'documentUrl',
      render: (value) =>
        value ? (
          <Link to={value.toString()} target="_blank" rel="noreferrer">
            <div className="flex justify-center items-center">
              <AiFillFilePdf className="text-red-500" size={24} />
            </div>
          </Link>
        ) : (
          <div className="flex justify-center items-center">
            <AiFillFilePdf className="text-gray-500" size={24} />
          </div>
        ),
    },
  ];

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!data.length && !isLoading) return <div>No hay datos</div>;

  const handleRowClick = (row: CriticalStandard) => {
    navigate(`/dashboard/critical-standars/details/${row.id}`);
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Normas Críticas</h1>

        <Link to="/dashboard/critical-standars/add">
          <Button
            label="Agregar estándar crítico"
            icon={<FiPlus />}
            onClick={() => {}}
          />
        </Link>
      </div>

      <Table
        data={data}
        columns={criticalStandardColumns}
        onRowClick={handleRowClick}
      />
      <LoadMoreButton hasMore={hasMore} isLoadingMore={isLoadingMore} onLoadMore={loadMore} />
    </div>
  );
};

export default CriticalStandarsPage;
